import { useEffect, useState } from 'react';
import { LifeBuoy, Loader2, AlertTriangle } from 'lucide-react';
import { api, ErrorApi } from '../api';
import FormularioReporte from './FormularioReporte';

/* =====================================================================
   Reportar por QR sin cuenta (R-TK4) · DIRECTRIZ-TICKETS F2
   =====================================================================
   La página que abre quien escanea la etiqueta de un activo y NO tiene
   cuenta —un visitante, un proveedor—. Solo reporta: no ve nada más que el
   nombre del activo. Vive fuera de la sesión (se monta en /reportar), así
   que aquí no hay `useAuth` ni menú ni datos de la empresa.

   El límite por minuto y el campo trampa anti-bot los pone el backend; el
   campo trampa se pinta aquí, fuera de la vista, para que solo un robot lo
   rellene.
   ===================================================================== */
function tokenDeLaUrl() {
  // /reportar/<token>  →  <token>
  const partes = (typeof window !== 'undefined' ? window.location.pathname : '')
    .split('/').filter(Boolean);
  const i = partes.indexOf('reportar');
  return i >= 0 && partes[i + 1] ? decodeURIComponent(partes[i + 1]) : '';
}

export default function ReportarPorQR() {
  const [token] = useState(tokenDeLaUrl);
  const [cargando, setCargando] = useState(true);
  const [etiqueta, setEtiqueta] = useState('');
  const [invalido, setInvalido] = useState(false);

  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState('media');
  const [trampa, setTrampa] = useState('');   // campo trampa: vacío en una persona
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [folio, setFolio] = useState('');

  useEffect(() => {
    let vivo = true;
    if (!token) { setCargando(false); setInvalido(true); return undefined; }
    api.get(`/tickets/reportar/${encodeURIComponent(token)}`)
      .then((d) => { if (vivo) { setEtiqueta(d.etiqueta); setCargando(false); } })
      .catch(() => { if (vivo) { setInvalido(true); setCargando(false); } });
    return () => { vivo = false; };
  }, [token]);

  async function enviar() {
    if (!descripcion.trim() || enviando) return;
    setEnviando(true);
    setError('');
    try {
      const r = await api.post(`/tickets/reportar/${encodeURIComponent(token)}`, {
        descripcion: descripcion.trim(),
        gravedad,
        sitio_web: trampa,   // vacío en una persona; el robot lo llena
      });
      setFolio(r.folio);
    } catch (e) {
      // El 429 (demasiados intentos) llega con su mensaje en español desde
      // el backend; los demás, con el suyo.
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo enviar el reporte. Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col gap-4 p-4">
      <header className="flex items-center gap-2 pt-2">
        <LifeBuoy className="h-6 w-6 text-[var(--color-marca)]" aria-hidden="true" />
        <span className="text-lg font-semibold text-gray-900">Reportar un problema</span>
      </header>

      {cargando && (
        <p className="flex items-center gap-2 py-10 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Abriendo el reporte…
        </p>
      )}

      {!cargando && invalido && (
        <div className="flex flex-col items-center gap-3 py-10 text-center text-gray-600">
          <div className="rounded-2xl bg-amber-100 p-4">
            <AlertTriangle className="h-8 w-8 text-amber-700" aria-hidden="true" />
          </div>
          <p className="font-semibold text-gray-900">Esta etiqueta no está activa</p>
          <p className="max-w-xs text-sm">
            El código no corresponde a ninguna etiqueta de TEGO en uso. Revisa
            que escaneaste una etiqueta de TEGO, o pide al encargado que la
            vuelva a imprimir.
          </p>
        </div>
      )}

      {!cargando && !invalido && (
        <>
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            Estás reportando sobre: <strong className="text-gray-900">{etiqueta}</strong>
          </p>
          <FormularioReporte
            descripcion={descripcion}
            onDescripcion={setDescripcion}
            gravedad={gravedad}
            onGravedad={setGravedad}
            enviando={enviando}
            error={error}
            folio={folio}
            onEnviar={enviar}
          />
          {/* CAMPO TRAMPA anti-bot. Fuera de la vista y del lector, pero en
              el árbol: un robot que rellena todo lo pone; una persona no lo
              ve. `aria-hidden` y `tabIndex=-1` para que nadie lo alcance sin
              querer. */}
          <div aria-hidden="true"
               style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
            <label htmlFor="sitio_web">No llenar</label>
            <input id="sitio_web" name="sitio_web" type="text" tabIndex={-1}
                   autoComplete="off" value={trampa}
                   onChange={(e) => setTrampa(e.target.value)} />
          </div>
        </>
      )}
    </div>
  );
}
